import {
  type FC,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

type CollapsibleContextType = {
  expanded: boolean;
  toggle: () => void;
};

type CollapsibleProps = {
  children: ReactNode;
  expanded?: boolean;
  className?: string;
};

type CollapsibleTriggerProps = {
  children:
    | ReactNode
    | (({ expanded }: Omit<CollapsibleContextType, 'toggle'>) => ReactNode);
  className?: string;
};
type CollapsibleContentProps = {
  children:
    | ReactNode
    | (({ toggle }: Omit<CollapsibleContextType, 'expanded'>) => ReactNode);
};

const CollapsibleContext = createContext<CollapsibleContextType>(
  {} as CollapsibleContextType,
);
const useCollapsible = () => useContext(CollapsibleContext);

const Collapsible = ({ children, expanded, className }: CollapsibleProps) => {
  const [expandedState, setExpandedState] = useState(expanded ?? false);

  const toggle = useCallback(() => {
    setExpandedState((prev) => !prev);
  }, []);

  const value = useMemo(
    () => ({
      expanded: expandedState,
      toggle,
    }),
    [expandedState, toggle],
  );

  return (
    <CollapsibleContext.Provider value={value}>
      <div className={className}>{children}</div>
    </CollapsibleContext.Provider>
  );
};

const CollapsibleTrigger: FC<CollapsibleTriggerProps> = ({
  children,
  className,
}) => {
  const { toggle, expanded } = useCollapsible();

  return (
    <div className={className} onKeyDown={toggle} onClick={toggle}>
      {typeof children === 'function' ? children({ expanded }) : children}
    </div>
  );
};

const CollapsibleContent: FC<CollapsibleContentProps> = ({ children }) => {
  const { expanded, toggle } = useCollapsible();

  if (!expanded) {
    return null;
  }

  if (typeof children === 'function') {
    return children({ toggle });
  }

  return children;
};

Collapsible.Trigger = CollapsibleTrigger;
Collapsible.Content = CollapsibleContent;

export default Collapsible;
